import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { FileText, Search, Users, Copy, ExternalLink, X } from 'lucide-react';
import { Grid, Card } from '../../styles/GlobalStyles';
import entrevistasData from '../../data/entrevistas-data.json';

// Container principal seguindo o padrão IndicadoresPerfil
const EntrevistasContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`;

const ContentWrapper = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

// Container de filtros seguindo o padrão
const FilterContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing.sm};
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

const SearchGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  min-width: 300px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: unset;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.surface};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 16px; /* Evita zoom em iOS */
    padding: ${({ theme }) => theme.spacing.lg}
      ${({ theme }) => theme.spacing.md};
  }
`;

const ActionButton = styled.button<{
  variant?: 'primary' | 'secondary' | 'danger';
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ variant = 'primary', theme }) => {
    switch (variant) {
      case 'secondary':
        return `
          background-color: transparent;
          color: ${theme.colors.warning};
          border: 1px solid ${theme.colors.warning};
          
          &:hover {
            background-color: ${theme.colors.warning}10;
          }
        `;
      case 'danger':
        return `
          background-color: ${theme.colors.error};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.error}dd;
          }
        `;
      default:
        return `
          background-color: ${theme.colors.primary};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.primaryDark};
          }
        `;
    }
  }}
`;

// Cards para formulários
const FormCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  height: auto;
  border: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const FormHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const FormIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background-color: ${({ color }) => color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    color: ${({ color }) => color};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 40px;
    height: 40px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const FormContent = styled.div`
  flex: 1;
`;

const FormTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    line-height: 1.4;
  }
`;

const FormSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const FormMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FormMetaItem = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

// Tabelas seguindo o padrão
const TableSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: ${({ theme }) => theme.spacing['2xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    gap: ${({ theme }) => theme.spacing.xs};
  }
`;

const TableContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px; /* Garante largura mínima para scroll horizontal */

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 500px;
  }
`;

const TableHeader = styled.thead`
  background-color: ${({ theme }) => theme.colors.surfaceLight};
`;

const TableHeaderCell = styled.th`
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceLight};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  vertical-align: middle;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const TableActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

const SmallButton = styled.button<{
  variant?: 'primary' | 'secondary' | 'success';
}>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${({ variant = 'primary', theme }) => {
    switch (variant) {
      case 'secondary':
        return `
          background-color: ${theme.colors.text.muted}15;
          color: ${theme.colors.text.secondary};
          
          &:hover {
            background-color: ${theme.colors.text.muted}25;
          }
        `;
      case 'success':
        return `
          background-color: ${theme.colors.success};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.success}dd;
          }
        `;
      default:
        return `
          background-color: ${theme.colors.primary};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.primaryDark};
          }
        `;
    }
  }}
`;

// Paginação
const PaginationContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background-color: ${({ theme }) => theme.colors.surfaceLight};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.md};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.sm};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const PaginationInfo = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    text-align: center;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ active, theme }) =>
    active ? theme.colors.primary : theme.colors.surface};
  color: ${({ active, theme }) =>
    active ? 'white' : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ active, theme }) =>
      active ? theme.colors.primaryDark : theme.colors.surfaceLight};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Grids seguindo o padrão
const FormsGrid = styled(Grid)`
  margin-bottom: 4rem;
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 3rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

export const Entrevistas: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [entrevistasPendentesPage, setEntrevistasPendentesPage] = useState(1);
  const [documentosPendentesPage, setDocumentosPendentesPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const itemsPerPage = 5;

  // Hook para detectar tamanho da tela
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Filtrar formulários baseado na busca
  const filteredFormularios = useMemo(() => {
    if (!searchTerm) return entrevistasData.formularios;

    return entrevistasData.formularios.filter(
      (formulario) =>
        formulario.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formulario.descricao.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  // Dados das tabelas (não filtrados pela busca)
  const filteredEntrevistas = entrevistasData.entrevistasPendentes;
  const filteredDocumentos = entrevistasData.documentosPendentes;

  // Paginação
  const paginatedEntrevistas = useMemo(() => {
    const startIndex = (entrevistasPendentesPage - 1) * itemsPerPage;
    return filteredEntrevistas.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredEntrevistas, entrevistasPendentesPage]);

  const paginatedDocumentos = useMemo(() => {
    const startIndex = (documentosPendentesPage - 1) * itemsPerPage;
    return filteredDocumentos.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredDocumentos, documentosPendentesPage]);

  const totalEntrevistasPages = Math.ceil(
    filteredEntrevistas.length / itemsPerPage
  );
  const totalDocumentosPages = Math.ceil(
    filteredDocumentos.length / itemsPerPage
  );

  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link);
    // Aqui você poderia adicionar uma notificação de sucesso
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setEntrevistasPendentesPage(1);
    setDocumentosPendentesPage(1);
  };

  return (
    <EntrevistasContainer>
      <ContentWrapper>
        <PageHeader>
          <PageTitle>Entrevistas</PageTitle>
        </PageHeader>

        {/* Filtros de busca */}
        <FilterContainer>
          <SearchGroup>
            <Search size={20} color="#64748B" />
            <SearchInput
              type="text"
              placeholder={
                isMobile
                  ? 'Pesquise formulários...'
                  : 'Pesquise formulários por título ou descrição...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchGroup>
          <ActionButton onClick={handleClearFilters}>
            <Search size={16} />
            Buscar
          </ActionButton>
          <ActionButton variant="secondary" onClick={handleClearFilters}>
            <X size={16} />
            Limpar filtros
          </ActionButton>
        </FilterContainer>

        {/* Formulários disponíveis */}
        <TableSection>
          <SectionTitle>
            <FileText size={24} />
            Formulários de Entrevista
            {searchTerm && (
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: 'normal',
                  color: '#64748B',
                }}
              >
                ({filteredFormularios.length} resultado
                {filteredFormularios.length !== 1 ? 's' : ''})
              </span>
            )}
          </SectionTitle>
          {filteredFormularios.length > 0 ? (
            <FormsGrid columns={2} minColumnWidth="500px">
              {filteredFormularios.map((formulario) => (
                <FormCard key={formulario.id}>
                  <FormHeader>
                    <FormIcon color={formulario.cor}>
                      <FileText size={24} />
                    </FormIcon>
                    <FormContent>
                      <FormTitle>{formulario.titulo}</FormTitle>
                      <FormSubtitle>{formulario.descricao}</FormSubtitle>
                      <FormMeta>
                        <FormMetaItem>
                          {formulario.perguntas} perguntas
                        </FormMetaItem>
                      </FormMeta>
                      <ActionButton>
                        <ExternalLink size={16} />
                        Responder
                      </ActionButton>
                    </FormContent>
                  </FormHeader>
                </FormCard>
              ))}
            </FormsGrid>
          ) : (
            <div
              style={{
                padding: '2rem',
                textAlign: 'center',
                color: '#64748B',
                background: '#F8FAFC',
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
              }}
            >
              <FileText
                size={48}
                style={{ color: '#CBD5E1', marginBottom: '1rem' }}
              />
              <p style={{ fontSize: '16px', marginBottom: '0.5rem' }}>
                Nenhum formulário encontrado
              </p>
              <p style={{ fontSize: '14px', margin: '0' }}>
                Tente ajustar os termos da sua pesquisa
              </p>
            </div>
          )}
        </TableSection>

        {/* Entrevistas Pendentes */}
        <TableSection>
          <SectionTitle>
            <Users size={24} />
            Entrevistas Pendentes
          </SectionTitle>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Colaborador</TableHeaderCell>
                  <TableHeaderCell>Filial</TableHeaderCell>
                  <TableHeaderCell>Responsável pela Entrevista</TableHeaderCell>
                  <TableHeaderCell>Ações</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {paginatedEntrevistas.map((entrevista) => (
                  <TableRow key={entrevista.id}>
                    <TableCell>{entrevista.colaborador}</TableCell>
                    <TableCell>{entrevista.filial}</TableCell>
                    <TableCell>{entrevista.responsavel}</TableCell>
                    <TableCell>
                      <TableActions>
                        <SmallButton>
                          <ExternalLink size={14} />
                          Responder
                        </SmallButton>
                      </TableActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationContainer>
              <PaginationInfo>
                Rows per page: {itemsPerPage} |{' '}
                {(entrevistasPendentesPage - 1) * itemsPerPage + 1} de{' '}
                {filteredEntrevistas.length}
              </PaginationInfo>
              <PaginationControls>
                <PaginationButton
                  onClick={() =>
                    setEntrevistasPendentesPage(
                      Math.max(1, entrevistasPendentesPage - 1)
                    )
                  }
                  disabled={entrevistasPendentesPage === 1}
                >
                  ‹
                </PaginationButton>
                <PaginationButton
                  onClick={() =>
                    setEntrevistasPendentesPage(
                      Math.min(
                        totalEntrevistasPages,
                        entrevistasPendentesPage + 1
                      )
                    )
                  }
                  disabled={entrevistasPendentesPage === totalEntrevistasPages}
                >
                  ›
                </PaginationButton>
              </PaginationControls>
            </PaginationContainer>
          </TableContainer>
        </TableSection>

        {/* Documentos Pendentes de Assinatura */}
        <TableSection>
          <SectionTitle>
            <FileText size={24} />
            Documentos pendentes de assinatura
          </SectionTitle>
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Formulário</TableHeaderCell>
                  <TableHeaderCell>Colaborador</TableHeaderCell>
                  <TableHeaderCell>Pendente desde</TableHeaderCell>
                  <TableHeaderCell>Ações</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {paginatedDocumentos.map((documento) => (
                  <TableRow key={documento.id}>
                    <TableCell>{documento.formulario}</TableCell>
                    <TableCell>{documento.colaborador}</TableCell>
                    <TableCell>{documento.pendentDesde}</TableCell>
                    <TableCell>
                      <TableActions>
                        <SmallButton
                          variant="secondary"
                          onClick={() => handleCopyLink(documento.link)}
                        >
                          <Copy size={14} />
                          Copiar link
                        </SmallButton>
                      </TableActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationContainer>
              <PaginationInfo>
                Rows per page: {itemsPerPage} |{' '}
                {(documentosPendentesPage - 1) * itemsPerPage + 1} de{' '}
                {filteredDocumentos.length}
              </PaginationInfo>
              <PaginationControls>
                <PaginationButton
                  onClick={() =>
                    setDocumentosPendentesPage(
                      Math.max(1, documentosPendentesPage - 1)
                    )
                  }
                  disabled={documentosPendentesPage === 1}
                >
                  ‹
                </PaginationButton>
                <PaginationButton
                  onClick={() =>
                    setDocumentosPendentesPage(
                      Math.min(
                        totalDocumentosPages,
                        documentosPendentesPage + 1
                      )
                    )
                  }
                  disabled={documentosPendentesPage === totalDocumentosPages}
                >
                  ›
                </PaginationButton>
              </PaginationControls>
            </PaginationContainer>
          </TableContainer>
        </TableSection>
      </ContentWrapper>
    </EntrevistasContainer>
  );
};
